import _ from 'lodash';
import { type AnalyticsData, type LoginEvent, type RequestEvent, type ActivityEvent } from './portal';

export function getLastDays(days: number): string[] {
  const dates: string[] = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    dates.push(date.toISOString().split('T')[0]); // "YYYY-MM-DD"
  }
  return dates;
}

// Parses "YYYY-MM-DD" as a local calendar date. new Date("YYYY-MM-DD") parses as UTC midnight, which is the
// previous day in time zones west of UTC.
export function dayToDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function analyticsDataToFrontendFormat(analyticsData: AnalyticsData) {
  return {
    recentActivityLog: analyticsData.recentActivityLog.map((event) => ({
      ...event,
      client_item_id: +event.client_item_id,
      type: event.is_comment ? 'comment' : 'auth',
    })),
    loginsPerDay: _.keyBy(analyticsData.loginsPerDay, 'day'),
    successRequestsPerDay: _.keyBy(analyticsData.successRequestsPerDay, 'day'),
    errorRequestsPerDay: _.keyBy(analyticsData.errorRequestsPerDay, 'day'),
    healthcheckLoginsPerDay: _.keyBy(analyticsData.healthcheckLoginsPerDay, 'day'),
  };
}

export type FrontendAnalyticsData = ReturnType<typeof analyticsDataToFrontendFormat>;

export function ratioToPercentage(ratio: number): string {
  return (ratio * 100).toFixed(2) + '%';
}

export function getUptimeColor(percentage: number): string {
  if (percentage >= 99.5) return '#00c875';
  if (percentage >= 99) return '#aacc27';
  if (percentage >= 92) return '#ffcb00';
  return '#df2f4a';
}

export function getActivityChartData(analyticsData: FrontendAnalyticsData, daysCount: number) {
  const days = getLastDays(daysCount);

  return days.map((day) => {
    const loginsCount = analyticsData.loginsPerDay[day] ? analyticsData.loginsPerDay[day].logins_count : 0;
    const successRequestsCount = analyticsData.successRequestsPerDay[day] ? analyticsData.successRequestsPerDay[day].requests_count : 0;
    const errorRequestsCount = analyticsData.errorRequestsPerDay[day] ? analyticsData.errorRequestsPerDay[day].requests_count : 0;

    return {
      day,
      loginsCount,
      successRequestsCount,
      errorRequestsCount,
    };
  });
}

export type ActivityChartData = ReturnType<typeof getActivityChartData>;

export function summarizedActivityChartData(chartData: ActivityChartData) {
  return chartData.reduce(
    (acc, item) => {
      acc.loginsCount += item.loginsCount;
      acc.successRequestsCount += item.successRequestsCount;
      acc.errorRequestsCount += item.errorRequestsCount;
      return acc;
    },
    { loginsCount: 0, successRequestsCount: 0, errorRequestsCount: 0 },
  );
}
